import {
  ACESFilmicToneMapping,
  Camera,
  DirectionalLight,
  PMREMGenerator,
  Scene,
  SRGBColorSpace,
  WebGLRenderer,
} from 'three';
import { RoomEnvironment } from 'three/addons/environments/RoomEnvironment.js';

import { blockMatrix, screenProjection } from './gridCamera';
import { buildPedestal, tintPedestal, disposePedestalGeometry } from './pedestalMesh';
import { buildChest, tintChest, disposeChestGeometry } from './chestMesh';

/**
 * The prop kinds this stage can draw, and how each is built and lit.
 *
 * One stage rather than one per kind: they share a renderer, an environment map
 * and a frame, and a second transparent WebGL layer over the same board would
 * cost a second context for nothing. A body names its kind; everything else
 * about placement is identical, because placement comes from the cell.
 */
const PROPS = {
  pedestal: {
    build: buildPedestal,
    tint: (prop, body) => tintPedestal(prop, body.color, body.marker, body.ambient),
    dispose: disposePedestalGeometry,
  },
  chest: {
    build: buildChest,
    tint: (prop, body) => tintChest(prop, body.marker, body.ambient),
    dispose: disposeChestGeometry,
  },
};

/**
 * The pedestals, rendered as actual objects standing on the board.
 *
 * Everything before this drew them as a picture of an object: a sprite with one
 * baked camera, which is right on one row of a raked grid and wrong on every other,
 * and which turns to mush the moment you warp it to fit. Real geometry has no baked
 * camera to fight, so it is simply correct wherever it stands, at any grid settings
 * and any footprint — and it gets real shading, so it reads as machined metal rather
 * than as a decal.
 *
 * The stage is a transparent WebGL layer sitting between the painted floor and the
 * holograms. Each prop is placed from the four morphed corners of its own cell, which
 * are the same four points the map drew that cell with, so a plinth cannot drift from
 * the ground it stands on however the grid is warped.
 */
export class PedestalStage {
  /**
   * @param canvas   the transparent layer to draw into
   * @param onReady  called when a prop has finished loading and the frame that
   *                 asked for it is therefore out of date. The GLB arrives long
   *                 after the frame that requested it, and the board only redraws
   *                 on input — without this the pedestals appear on the first
   *                 mouse move and the board looks empty until someone touches it.
   */
  constructor(canvas, onReady = null, { record = false } = {}) {
    this.canvas = canvas;
    this.onReady = onReady;
    this.renderer = new WebGLRenderer({
      canvas,
      alpha: true,
      antialias: true,
      powerPreference: 'low-power',
      preserveDrawingBuffer: record,
    });
    this.renderer.setClearColor(0x000000, 0);
    this.renderer.outputColorSpace = SRGBColorSpace;
    // Each prop is a pass of its own, so the frame is cleared once by hand and then
    // painted into; letting three clear per render would leave only the last prop.
    this.renderer.autoClear = false;
    // The prop's own lighting is partly baked into its texture, and it was authored
    // for image-based light. Tone mapping keeps the metal off the clipping point.
    this.renderer.toneMapping = ACESFilmicToneMapping;
    // Measured on the map, which is a dark night scene: much above this the plate
    // goes chalk white and the plinths, not the figures, become what the board is
    // made of; much below it and the machining stops reading at all.
    this.renderer.toneMappingExposure = 1.0;

    this.scene = new Scene();
    /*
     * A plain Camera, given its matrix outright rather than a Perspective or
     * Orthographic one that would recompute it from its own parameters. There is no
     * pose to give such a camera: the projection is per prop, built from the cell it
     * stands on, and all that is left here is screen pixels to clip space.
     */
    this.camera = new Camera();
    this.camera.matrixAutoUpdate = false;

    this._lights();
    // One loaded model per kind, drawn once per body that asks for it.
    this.props = new Map();
    this.loading = new Set();
    this.viewport = { width: 0, height: 0 };
  }

  /** The pedestal, for callers that only ever wanted the one prop. */
  get prop() {
    return this.props.get('pedestal') || null;
  }

  /**
   * Image-based light, not a lone lamp.
   *
   * This prop's panel detail comes from what its metal reflects. Under a single
   * directional light it flattens into a dark lump — its own brief warns of it —
   * because a rough metal with nothing to reflect returns almost nothing. A room
   * environment gives every face something to pick up, and one key on top of it
   * keeps a readable highlight and a sense of direction that matches the board.
   *
   * Without the environment - at the brief's own suggestion of a lone lamp - the
   * plate renders as a grey lump indistinguishable from the rubble.
   *
   * It is kept deliberately dim. The environment is a bright room and this is a
   * night scene; at full strength it lights the plate from a place the board does
   * not have and the prop floats off the terrain. Most of the plate's actual colour
   * comes from the ground it is standing on - see `tintPedestal`. What the room is
   * left doing is the one thing a flat ambient cannot: giving the machined faces
   * something to catch, so the panels and edges read at all.
   */
  _lights() {
    const pmrem = new PMREMGenerator(this.renderer);
    this.scene.environment = pmrem.fromScene(new RoomEnvironment(), 0.04).texture;
    this.scene.environmentIntensity = 0.45;
    pmrem.dispose();

    const key = new DirectionalLight(0xdfefff, 1.15);
    key.position.set(-1.2, 2.4, 1.0);
    this.scene.add(key);
  }

  /**
   * How high the plate's top face is, in cells, or 0 before the prop has loaded.
   * The scene strikes the projection's cone from here, so the light comes out of the
   * object rather than out of where a sprite's lens used to be.
   */
  get plateTop() {
    return this.props.get('pedestal')?.userData.plateTop ?? 0;
  }

  /**
   * How high the emitter head's lens is, in cells, or 0 before the prop has loaded.
   * The plate is where a figure stands; this is where the light is thrown from, and
   * the two are half a prop apart.
   */
  get emitterTop() {
    return this.props.get('pedestal')?.userData.emitterTop ?? 0;
  }

  /** Match the drawing buffer to the map canvas. */
  resize(width, height, pixelRatio = window.devicePixelRatio || 1) {
    if (!(width > 0) || !(height > 0)) return;
    if (this.viewport.width === width && this.viewport.height === height) return;
    this.viewport = { width, height };
    this.renderer.setPixelRatio(Math.min(pixelRatio, 2));
    this.renderer.setSize(width, height, false);
  }

  /**
   * Draw one frame.
   *
   * One prop is loaded and drawn once per body, rather than one prop per body: they
   * are the same object standing in different cells, and the only thing that differs
   * between them is where the cell puts it and what colour its trim is.
   *
   * Each body gets its own pass because each body gets its own PROJECTION. The map
   * has no single camera to share (see gridCamera), so the projection is built from
   * the cell — and it has to live in the camera rather than in the object's matrix:
   * lighting is computed from the inverse-transpose of the model-view matrix, and a
   * matrix carrying a perspective divide turns every normal to nonsense. Put there,
   * the plate shaded flat black however much light was thrown at it. The object's
   * own matrix is therefore kept rigid and the projection does all the warping.
   *
   * @param bodies  one entry per prop, each carrying the map's own geometry for the
   *                block it stands on:
   *                {span, color, marker, ambient, quad, heightScale, depth}
   *                - quad: the block's four morphed corners, in device pixels
   *                - heightScale: that block's on-screen cell width
   *                - depth: how near the viewer it is, for draw order
   *                - marker: status the plate's emission brightens for
   *                - ambient: the colour of the ground it is standing on
   */
  render(bodies) {
    if (!this.viewport.width) return;

    const toClip = screenProjection(this.viewport);
    if (!toClip) return;

    this.renderer.clear();

    // Ask for whatever the frame needs and has not got yet. A body whose model
    // is still in flight is simply skipped this frame; `onReady` brings the
    // board back once it lands.
    for (const kind of new Set(bodies.map(body => body.kind || 'pedestal'))) {
      if (!this.props.has(kind)) this._mount(kind);
    }

    // Back to front, so a nearer plinth overlaps the one behind it. The passes are
    // sequential and the depth buffer is cleared between them, so this IS the order
    // they appear in - a painter's algorithm, with each prop internally depth-sorted
    // against itself and against nothing else.
    const ordered = [...bodies].sort((p, q) => p.depth - q.depth);
    for (const body of ordered) {
      const kind = body.kind || 'pedestal';
      const prop = this.props.get(kind);
      const spec = PROPS[kind];
      if (!prop || !spec) continue;

      const span = Math.max(1, body.span || 1);
      const placement = blockMatrix(body.quad, span, body.heightScale);
      if (!placement) continue;

      // Object space is the block and its units are cells, so a prop seated to its
      // own footprint covers that fraction of whatever block it is standing on.
      prop.scale.setScalar(span);

      // Only the prop being drawn may be in the scene. All kinds share one scene
      // and one camera pass, so leaving the others attached would paint every
      // prop at every body's placement.
      this._show(prop);

      // No inverse is kept: the matrix is singular by design - depth is flat - and
      // inverting it only earns a warning per prop per frame. Nothing here raycasts.
      this.camera.projectionMatrix.multiplyMatrices(toClip, placement);

      spec.tint(prop, body);
      this.renderer.clearDepth();
      this.renderer.render(this.scene, this.camera);
    }
  }

  /** Make `prop` the only prop in the scene. */
  _show(prop) {
    for (const candidate of this.props.values()) {
      candidate.visible = candidate === prop;
    }
  }

  /**
   * Fetch the prop, once.
   *
   * The frame that asks for it draws without it; `onReady` brings the board back
   * once it has arrived. Marked in flight immediately, so a board of thirty tokens
   * does not queue thirty loads of the same file.
   */
  _mount(kind) {
    const spec = PROPS[kind];
    if (!spec || this.loading.has(kind)) return;
    this.loading.add(kind);

    spec.build()
      .then(prop => {
        // The frustum is derived from a projection with no usable depth, so three's
        // own culling test cannot be trusted to keep the prop on screen.
        prop.traverse(part => { part.frustumCulled = false; });
        this.props.set(kind, prop);
        this.scene.add(prop);
        if (this.onReady) this.onReady();
      })
      .catch(error => {
        this.loading.delete(kind);
        console.warn(`[PedestalStage] could not load the ${kind}:`, error);
      });
  }

  dispose() {
    for (const prop of this.props.values()) this.scene.remove(prop);
    this.props.clear();
    this.loading.clear();
    Object.values(PROPS).forEach(spec => spec.dispose());
    this.renderer.dispose();
  }
}

export default PedestalStage;
